import { useEffect, useId, useRef, useState } from 'react';
import { Switch } from '@voll-intelligence/ui-kit/ui/switch';
import { CheckIcon } from '@phosphor-icons/react';
import { AirlineLogo } from '../../../shared/airline_logo/airline_logo.js';
import {
  activeAxisCount,
  airlineOptions,
  airportOptions,
  defaultFilterState,
  optionCounts,
  priceBounds,
  type CityOfAirport,
  type FlightFilterOptionCounts,
  type FlightFilterState,
  type OptionCount,
  type StopsChoice,
} from '../filter_logic.js';
import type { FlightOfferSource } from '../flight_offer_source.js';
import type { FlightFilterCopy } from './filter_copy.js';
import { fmtMoneyWhole } from '../flight_format.js';
import { PanelHead, PanelSection, wantsHead } from './filter_panel_chrome.js';
import {
  cabinPolicyMark,
  longestFlightMinutes,
  type CabinPolicyMark,
  type FlightCabinPolicy,
} from './cabin_policy.js';
import { NumberSection } from './number_section.js';
import { PriceSection, TimeSection } from './range_sections.js';
import './filter_panel.css';

/**
 * FlightFilterPanel — a UI dos eixos do filtro unificado de voos. Dois hosts:
 * dentro do FlightFilterDrawer do chat (`variant="drawer"`) e coluna fixa da
 * bancada determinística (`variant="column"`). Stateless: recebe
 * `FlightFilterState` e devolve o próximo por `onChange`; contagens derivadas
 * internamente via `optionCounts` — aeroporto e paradas, os dois eixos com
 * escopo de página (ver `FlightFilterOptionCounts`). Opção com contagem 0 fica
 * desabilitada, nunca some; os outros eixos não desabilitam por zero porque não
 * têm número de conjunto pra provar ausência. "Redefinir todos" sempre
 * presente, desabilitado sem eixo ativo.
 *
 * **A CABINE é a exceção declarada**: ela entra pelo seam `cabin` e REFAZ a busca em
 * vez de refinar a que está em cena (ver `CabinSection`). Mora no painel como
 * primeiro grupo, mas SEGUE fora do alcance do `Redefinir` — a exceção é de
 * comportamento, não de lugar.
 *
 * Namespace CSS próprio `.ffp-*` — NUNCA `.fq-*` (re-skina bus_offers) nem
 * `.fd-*` (casca do drawer).
 */
/** A cabine do PEDIDO de busca (o `cabin` do `FlightSearchParams` do host) —
 *  não há cabine na oferta. */
export type FlightSearchCabin = 'economy' | 'premium' | 'business' | 'first';

/**
 * A cabine de um pedido SEM cabine. Não é escolha de desenho: omitir `cabinType`
 * no fio devolve ZERO item no provider, então `voll-apollo` manda `ECONOMY` quando
 * o pedido não traz cabine (`CABIN_ENUM`, `subgraphs/flight/ops/search.ts`). Um
 * alvo "Qualquer" aqui prometeria um conjunto que a busca nunca traz — a coluna
 * mostra Econômica ligada porque é isso que o servidor pesquisou.
 */
const DEFAULT_CABIN: FlightSearchCabin = 'economy';

/**
 * O seam da CABINE. Nome deliberado (`onSearch`, não `onChange`): quem recebe isto
 * dispara uma BUSCA nova, não um corte sobre a busca em cena — ver `CabinSection`.
 * Ausente ⇒ a seção não existe (o drawer legado do card não tem seam de busca;
 * explorer congelado não tem busca a oferecer).
 */
export type FlightFilterCabin = {
  /** Ausente ⇒ o pedido não nomeou cabine, e o fio pesquisou `DEFAULT_CABIN`. */
  value?: FlightSearchCabin;
  /** Uma busca em voo: os alvos travam até a resposta pousar. */
  busy?: boolean;
  /** As cabines que a política do viajante permite (#6438). Ausente ⇒ nenhuma marca. */
  policy?: FlightCabinPolicy;
  onSearch: (cabin: FlightSearchCabin) => void;
};

/** O que o painel lê das facetas do SERVIDOR (`ModalSessionFlightFacets` do
 *  domain, pela forma; o genui-kit prova a compatibilidade num teste de tipo). */
export type FlightFilterFacets = {
  priceRange?: { min: number; max: number };
  airlines?: readonly { code: string; name: string }[];
};

export type FlightFilterPanelProps = {
  offers: readonly FlightOfferSource[];
  state: FlightFilterState;
  onChange: (next: FlightFilterState) => void;
  variant: 'drawer' | 'column';
  /** O cabeçalho próprio (`Filtros` + `Redefinir`). `false` quando a casca de
   *  fora já o desenha (a coluna do `ResultsLayout`, que tem heading e Redefinir). */
  header?: boolean;
  /** O fechar da folha de tela cheia do palco estreito: com ele o cabeçalho ganha
   *  o X (o mesmo `dismiss='close'` do `ResultsFiltersPanel` do kit) e nasce
   *  sempre — sem ele a folha só saía pelo "Aplicar" (#3844). */
  onClose?: () => void;
  cabin?: FlightFilterCabin;
  /**
   * Facetas do SERVIDOR (contagem e "a partir de" por opção, companhias do
   * conjunto, faixa de preço da busca inteira). Presentes, mandam nos eixos que o
   * provider filtra; ausentes, tudo é derivado das ofertas em mão — que é o caso
   * do host sem seam de refine.
   */
  facets?: FlightFilterFacets;
  /**
   * A BUSCA está em voo e não há oferta em mão: os eixos entram como ESQUELETO.
   * Não é o mesmo que lista vazia (`offers: []` sem busca), onde a coluna é só a
   * cabine de propósito — ver `AxesSkeleton`. Um refine em voo NÃO é isto: lá as
   * facetas e a lista de antes continuam em cena.
   */
  loading?: boolean;
  /** Os textos do painel — o genui-kit os monta com `flightFilterCopy(locale)`. */
  copy: FlightFilterCopy;
  /** O locale dos campos de valor (separador de milhar). */
  locale: string;
  /** A cidade de um IATA (agrupa os aeroportos) — o host passa o `cityByAirport`
   *  do domain. */
  cityOf: CityOfAirport;
  /** Um eixo mudou (`filter_applied` na telemetria do host). As réguas e o número
   *  do voo chegam só quando o gesto para (ver `DRAG_FACETS`). */
  onFacetApplied?: (facet: string) => void;
};

/**
 * O painel tem eixo a mostrar? É o portão do bloco de cortes: a coluna em si é
 * do `ResultsLayout` e existe sempre — o que nasce e some é o que vai dentro.
 *
 * Com corte ATIVO os eixos ficam em cena mesmo sem oferta nenhuma: o painel é a
 * única casa do "Redefinir", e escondê-lo quando o corte zera a lista deixava a
 * pessoa sem volta. Sem corte E sem oferta não há o que mostrar — os cartões
 * viriam todos zerados e o "Redefinir" já desabilitado.
 */
const hasCutAxes = (
  offers: readonly FlightOfferSource[],
  state: FlightFilterState,
  ceiling?: number,
): boolean => offers.length > 0 || activeAxisCount(offers, state, ceiling) > 0;

/** As quatro opções do desenho (#2111), na ordem do protótipo. Multi-seleção:
 *  cada linha é um checkbox, e a união das marcadas é o corte. */
const STOP_OPTS: ReadonlyArray<{
  key: StopsChoice;
  label: keyof Omit<FlightFilterCopy['stops'], 'label'>;
}> = [
  { key: 'nonstop', label: 'direct' },
  { key: 'up_to_1', label: 'one' },
  { key: 'up_to_2', label: 'two' },
  { key: 'more_than_2', label: 'above' },
];

/** As cabines pesquisáveis. Só valores do fio: a ausência de cabine não é um alvo
 *  aqui, é o `DEFAULT_CABIN` que o servidor pesquisa por ela. */
const CABIN_OPTS: readonly FlightSearchCabin[] = ['economy', 'premium', 'business', 'first'];

const toggle = <T,>(list: T[], item: T): T[] =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

/**
 * Eixo real do `FlightFilterState` → literal do REGISTRY (domain) de telemetria.
 * Mesmos literais em toda a fase 1 — o painel não inventa vocabulário próprio.
 * `Redefinir` some da tabela de propósito: zera VÁRIOS eixos de uma vez, e
 * "filter_applied" descreve a escolha de UM eixo, não o retorno ao neutro.
 */
const FACET_BY_FIELD: Partial<Record<keyof FlightFilterState, string>> = {
  airlines: 'airline',
  airports: 'airport',
  stops: 'stops',
  departureRange: 'time',
  arrivalRange: 'arrival_time',
  minPrice: 'price',
  maxPrice: 'price',
  withinPolicyOnly: 'policy',
  flightNumber: 'flight_number',
};

/** The search's currency: the first offer's, or the last one seen while the page
 *  is empty (kept during render, not in a ref). */
function useSearchCurrency(offers: FlightFilterPanelProps['offers']): string | undefined {
  const [last, setLast] = useState<string | undefined>(undefined);
  const inScene = offers[0]?.price.currency;
  if (inScene !== undefined && inScene !== last) setLast(inScene);
  return inScene ?? last;
}

/**
 * Os eixos que mudam por FLUXO contínuo — as réguas (preço, horários) por
 * arrasto e o número do voo por digitação. Um slider emite um evento por tick
 * (30 a 60 num arrasto) e um campo um por tecla, e um `filter_applied` por
 * tick inflaria a faceta a ponto de expulsar eventos de funil da fila de 100 do
 * host. O CORTE em si segue por tick (ver `patch`): só a TELEMETRIA espera a
 * borda de saída do gesto — o dedo parar de mexer por
 * `PRICE_TELEMETRY_DEBOUNCE_MS`.
 */
const DRAG_FACETS: ReadonlySet<string> = new Set([
  'price',
  'time',
  'arrival_time',
  'flight_number',
]);

const PRICE_TELEMETRY_DEBOUNCE_MS = 300;

type Patch = (p: Partial<FlightFilterState>) => void;

type SectionProps = {
  state: FlightFilterState;
  onPatch: Patch;
  copy: FlightFilterCopy;
};

export function FlightFilterPanel({
  offers,
  state,
  onChange,
  variant,
  header = true,
  onClose,
  cabin,
  facets,
  loading,
  copy,
  locale,
  cityOf,
  onFacetApplied,
}: Readonly<FlightFilterPanelProps>) {
  const counts = optionCounts(offers, state, cityOf);
  /**
   * O teto do eixo de preço é da BUSCA, não da lista em mão: quando é o corte de
   * preço que esvazia a página, derivar da lista apaga o próprio corte da contagem —
   * e com ele o cabeçalho e o "Redefinir", que é a única volta.
   */
  const ceiling = facets?.priceRange?.max ?? priceBounds(offers)[1];
  /**
   * A moeda também é da busca. Ela só existia na primeira oferta, então a página
   * vazia a deixava `undefined` e derrubava a seção de preço inteira junto — o mesmo
   * beco, por outro caminho. Uma busca não troca de moeda no meio.
   */
  const currency = useSearchCurrency(offers);
  // Só o preço arrasta (todo o resto é um clique = um corte = um evento): o
  // timer mora num ref (sobrevive a re-render sem reagendar) e é keyed por
  // faceta — cada eixo de arrasto (preço, saída, chegada) coalesce sozinho, e
  // um corte por clique nunca passa por aqui.
  const dragDebounce = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    const timers = dragDebounce.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);
  /** Cancela todo `filter_applied` de arrasto ainda pendente — chamado pelo
   *  `Redefinir` ANTES do `onChange`. Sem isto, um eixo mexido bem antes do
   *  reset ainda aterrissa 300ms depois: um evento fantasma descrevendo um corte
   *  que o próprio clique acabou de apagar do estado. */
  const cancelDragTelemetry = () => {
    for (const timer of dragDebounce.current.values()) clearTimeout(timer);
    dragDebounce.current.clear();
  };
  const patch: Patch = (p) => {
    // Cada seção chama `onPatch` com um EIXO por vez — a primeira chave do patch
    // identifica o eixo mexido, sem cada seção emitir sozinha. As duas pontas do
    // preço podem vir juntas (ponta digitada que cruza a outra, ver
    // `PriceSection`): ambas mapeiam a MESMA faceta.
    const [field] = Object.keys(p) as (keyof FlightFilterState)[];
    const facet = field ? FACET_BY_FIELD[field] : undefined;
    if (facet && DRAG_FACETS.has(facet)) {
      // Corte funcional por tick (o trilho não pode travar atrás da telemetria);
      // só o EVENTO espera o dedo parar — a borda de SAÍDA do arrasto, não a de
      // entrada, senão o primeiro tick já dispararia e os seguintes reagendariam
      // em vão.
      const pending = dragDebounce.current.get(facet);
      if (pending) clearTimeout(pending);
      dragDebounce.current.set(
        facet,
        setTimeout(() => {
          dragDebounce.current.delete(facet);
          onFacetApplied?.(facet);
        }, PRICE_TELEMETRY_DEBOUNCE_MS),
      );
    } else if (facet) {
      onFacetApplied?.(facet);
    }
    onChange({ ...state, ...p });
  };
  // Na espera o cabeçalho vem SEM eixo por baixo dele — quem o justifica ali é o
  // esqueleto, que promete os eixos que vêm.
  const axes = hasCutAxes(offers, state, ceiling);

  return (
    <div className={`ffp ffp--${variant}`}>
      {/* O cabeçalho também nasce quando só há CABINE (lista vazia, nenhum corte a
          desfazer): sem ele a cabine volta a flutuar colada no chrome do explorer,
          lendo como um bloco de outro assunto — a queixa que a moveu pra cá. O
          `Redefinir` nesse estado fica desabilitado, que é a verdade: não há corte.
          Na ESPERA o cabeçalho volta pelo mesmo motivo de sempre — o que está
          embaixo dele é esqueleto, e a coluna precisa nascer com o desenho que vai
          ter. Ver `hasCutAxes`. */}
      {wantsHead(header, loading, axes, cabin, Boolean(onClose)) && (
        <PanelHead
          offers={offers}
          state={state}
          ceiling={ceiling}
          loading={loading}
          copy={copy}
          {...(onClose ? { onClose } : {})}
          onReset={() => {
            cancelDragTelemetry();
            onChange(defaultFilterState(offers, ceiling));
          }}
        />
      )}
      {/* PRIMEIRO grupo, e fora do bloco de eixos: ela sobrevive à lista vazia
          porque é o único caminho de volta de uma busca sem resultado (uma busca
          por Primeira classe zera a lista com facilidade). */}
      {cabin && <CabinSection cabin={cabin} copy={copy} offers={offers} />}
      {loading && <AxesSkeleton />}
      {/* A ORDEM é a do protótipo (#2110/#2111): política, paradas, companhias,
          aeroportos, horários, preço e número do voo. */}
      {!loading && axes && (
        <>
          <PolicySection offers={offers} state={state} onPatch={patch} copy={copy} />
          <StopsSection
            state={state}
            counts={counts}
            currency={currency}
            onPatch={patch}
            copy={copy}
          />
          <AirlineSection
            offers={offers}
            state={state}
            currency={currency}
            onPatch={patch}
            serverAirlines={facets?.airlines}
            copy={copy}
          />
          <AirportSection
            offers={offers}
            state={state}
            counts={counts}
            onPatch={patch}
            copy={copy}
            cityOf={cityOf}
          />
          <TimeSection state={state} onPatch={patch} copy={copy} />
          <PriceSection
            offers={offers}
            state={state}
            currency={currency}
            onPatch={patch}
            bounds={facets?.priceRange}
            copy={copy}
            locale={locale}
          />
          <NumberSection state={state} onPatch={patch} copy={copy} />
        </>
      )}
    </div>
  );
}

/**
 * O esqueleto dos eixos — a coluna durante a BUSCA.
 *
 * Os eixos saem das ofertas (contagem, faixa de preço, companhias da rota), então
 * durante a busca não existe eixo nenhum a desenhar: o que a coluna mostrava era a
 * cabine e branco por 12s a 40s, ao lado de uma lista inteira de esqueleto.
 *
 * A fila reproduz a ORDEM e a MASSA dos eixos de verdade (`política`, `paradas`,
 * `companhias`, `horários`, `preço`, `número do voo`), aproximadas do painel
 * montado: o switch da política (~24px), 4 linhas de check de paradas e de
 * companhias (~30px), as duas réguas do horário (~54px cada), o bloco de preço
 * (histograma + régua + campos, ~110px) e o campo do número (~36px). Reusa
 * `.ffp-group` — recuo e fio de separação são os mesmos do eixo real, senão o
 * esqueleto anuncia um ritmo que a coluna não tem.
 *
 * `aria-hidden`: quem anuncia a espera é o `<output role="status">` da lista
 * ("Buscando voos…"); um segundo anúncio aqui seria a mesma notícia duas vezes.
 */
const SKEL_AXES: ReadonlyArray<{ rows: number; h: number }> = [
  { rows: 1, h: 24 },
  { rows: 4, h: 30 },
  { rows: 4, h: 31 },
  { rows: 2, h: 54 },
  { rows: 1, h: 110 },
  { rows: 1, h: 36 },
];

function AxesSkeleton() {
  return (
    <div className="ffp-skel" aria-hidden="true">
      {SKEL_AXES.map((axis) => (
        <div key={`${String(axis.rows)}-${String(axis.h)}`} className="ffp-group">
          <span className="ffp-skel-bar is-label" />
          {Array.from({ length: axis.rows }, (_, r) => (
            <span key={r} className="ffp-skel-bar" style={{ height: axis.h }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * **Cabine — o único controle desta coluna que REFAZ a busca em vez de refinar a
 * que está em cena.**
 *
 * Os eixos de corte viram `refine`: mesma sessão, mesmo `searchKey`, a lista volta
 * refiltrada e o `Redefinir` desfaz. A cabine não cabe nesse molde, e não é
 * omissão do `filter_logic`: ela é parâmetro (e echo) da BUSCA, não atributo da
 * oferta — não está na oferta, nem no segmento, nem na tarifa. Trocá-la é pedir
 * OUTRA busca ao provider: `searchKey` novo, lista e fases zeradas, e os cortes
 * redefinidos junto (eles descreviam o inventário anterior).
 *
 * A diferença tem de estar VISÍVEL, senão a coluna vira armadilha (um controle que
 * parece corte e troca a busca por baixo). Ela é dita em dois canais:
 *   ① a frase — "Trocar a cabine refaz a busca", junto dos alvos;
 *   ② o comportamento — a opção LIGADA fica INERTE, o inverso da régua do
 *      linha de corte (lá o corte ativo tem de continuar clicável, porque re-clicar
 *      é como se desfaz o refine). Aqui re-clicar não desfaz nada: gastaria provider
 *      pra repetir a lista da tela e ainda derrubaria os cortes em vigor.
 *
 * **Havia um terceiro, e ele foi retirado de propósito** (revisão de UX,
 * 2026-08-05): a cabine ficava ACIMA do cabeçalho `Filtros`, e o lugar dizia
 * "isto não é um corte". Custava caro — colada no chrome do explorer, ela lia como
 * um bloco de outro assunto, fora do painel. Hoje é o primeiro grupo DENTRO dele.
 *
 * O que o lugar dizia, o `Redefinir` continua tendo de honrar por conta própria: a
 * cabine NÃO entra em `FlightFilterState`, `defaultFilterState` nem
 * `activeAxisCount`. Somá-la ali faria o `Redefinir` disparar uma re-busca no
 * provider a cada clique — a regressão que a mudança de lugar convida, e que o
 * `onChange` explodindo nos testes desta seção existe para pegar.
 *
 * Inerte é `aria-disabled` + clique ignorado no handler, NUNCA o `disabled` do DOM:
 * a opção ligada é quem carrega qual cabine produziu esta lista, e `disabled` a
 * tiraria do tab order e da leitura de tecnologia assistiva. Durante a busca é pior
 * ainda — desabilitar o botão que acabou de ser clicado joga o foco no `body` e o
 * teclado perde o lugar.
 *
 * Com a política do viajante (`cabin.policy`, #6438), a cabine fora dela ganha borda
 * tracejada e uma linha que diz por quê — e continua pesquisável: a policy-api tem
 * exceções por rota que o seletor não enxerga. A executiva "a partir de N horas" mede
 * o voo mais longo da lista em cena; sem política, nada muda.
 */
function CabinSection({
  cabin,
  copy,
  offers,
}: Readonly<{
  cabin: FlightFilterCabin;
  copy: FlightFilterCopy;
  offers: readonly FlightOfferSource[];
}>) {
  const noteId = useId();
  const longest = longestFlightMinutes(offers);
  const marks = CABIN_OPTS.map((value) => ({
    value,
    mark: cabinPolicyMark(cabin.policy, value, longest),
  }));
  return (
    <PanelSection label={copy.cabin.label}>
      <fieldset className="ffp-cabins" aria-label={copy.cabin.label}>
        {marks.map(({ value, mark }) => {
          // Pedido sem cabine ⇒ o alvo ligado é o `DEFAULT_CABIN`, que é a cabine
          // que o fio pesquisou de verdade.
          const on = (cabin.value ?? DEFAULT_CABIN) === value;
          const inert = on || Boolean(cabin.busy);
          const out = isOut(mark);
          return (
            <button
              key={value}
              type="button"
              className={`ffp-cabin${on ? ' is-on' : ''}${out ? ' is-out' : ''}`}
              aria-pressed={on}
              aria-disabled={inert}
              {...(mark ? { 'aria-describedby': `${noteId}-${value}` } : {})}
              onClick={() => {
                if (inert) return;
                cabin.onSearch(value);
              }}
            >
              {copy.cabin[value]}
            </button>
          );
        })}
      </fieldset>
      <CabinPolicyNotes marks={marks} copy={copy} noteId={noteId} />
      <span className="ffp-research-note">{copy.cabin.refetch}</span>
    </PanelSection>
  );
}

const isOut = (mark: CabinPolicyMark | null): boolean =>
  mark?.kind === 'out' || (mark?.kind === 'from_hours' && !mark.met);

/** O porquê de cada marca, uma linha por cabine marcada. A classe fora da política
 *  continua clicável (decisão de produto, #6438): a linha é o que a explica. */
function CabinPolicyNotes({
  marks,
  copy,
  noteId,
}: Readonly<{
  marks: readonly { value: FlightSearchCabin; mark: CabinPolicyMark | null }[];
  copy: FlightFilterCopy;
  noteId: string;
}>) {
  const noted = marks.filter((m) => m.mark !== null);
  if (noted.length === 0) return null;
  const text = (mark: CabinPolicyMark): string => {
    if (mark.kind === 'out') return copy.cabin.outOfPolicy;
    return mark.met
      ? copy.cabin.allowedFromHours(mark.hours)
      : copy.cabin.outBelowHours(mark.hours);
  };
  return (
    <ul className="ffp-cabin-policy">
      {noted.map(({ value, mark }) => (
        <li key={value} id={`${noteId}-${value}`}>
          <strong>{copy.cabin[value]}</strong> {mark && text(mark)}
        </li>
      ))}
    </ul>
  );
}

/** Contagem na ponta da linha de aeroporto — o mock (2110) mostra só o número;
 *  o custo em preço vive nas linhas de paradas e companhias. */
function Cost({ value }: Readonly<{ value: OptionCount }>) {
  return <span className="ffp-cost">{value.n}</span>;
}

/**
 * Política é um TOGGLE único (#2111) — o eixo tem uma pergunta só ("apenas
 * dentro?"), e o cartão-opção lia como uma lista de um item. O `Switch` é o do
 * DS; o embrulho `genui2` LOCAL é o que dá tokens a ele no drawer legado do
 * chat, que não tem a raiz `.genui2` por cima (mesmo padrão do popover
 * portado do `PlaceField`).
 *
 * Página SEM veredito de política (produtor legado): o corte só mantém status
 * `"in"`, então ligar transformaria um resultado válido em lista vazia — o
 * switch desabilita, honrando o contrato de `withinPolicyOnly`. A exceção é o
 * switch já LIGADO: ele fica vivo mesmo sem veredito em cena, porque é o único
 * caminho de desfazer (o próprio corte pode ter esvaziado a página dos
 * vereditos).
 */
function PolicySection({
  offers,
  state,
  onPatch,
  copy,
}: { offers: readonly FlightOfferSource[] } & SectionProps) {
  const labelId = useId();
  const hasVerdicts = offers.some((o) => o.policy !== undefined);
  return (
    <PanelSection label={copy.policy.label}>
      <div className="ffp-switchrow">
        <span className="genui2">
          <Switch
            checked={state.withinPolicyOnly}
            disabled={!hasVerdicts && !state.withinPolicyOnly}
            aria-labelledby={labelId}
            onCheckedChange={(on) => {
              onPatch({ withinPolicyOnly: on });
            }}
          />
        </span>
        <span id={labelId} className="ffp-switchlabel">
          {copy.policy.withinPolicy}
        </span>
      </div>
    </PanelSection>
  );
}

/**
 * Paradas viraram CHECKBOXES de multi-seleção com o menor preço da opção na
 * linha (#2111) — a mesma anatomia das linhas de companhia/aeroporto
 * (`.ffp-check`). O preço sai da PÁGINA em mão sem o próprio eixo
 * (`optionCounts`): a contagem de conjunto custava uma busca keyed por opção a
 * cada refine. Opção com zero na página desabilita (o desenho pede a linha
 * apagada), MENOS a marcada — ela precisa continuar clicável pra desfazer o
 * próprio corte.
 */
function StopsSection({
  state,
  counts,
  currency,
  onPatch,
  copy,
}: {
  counts: FlightFilterOptionCounts;
  currency: string | undefined;
} & SectionProps) {
  return (
    <PanelSection label={copy.stops.label}>
      <fieldset className="ffp-checks" aria-label={copy.stops.label}>
        {STOP_OPTS.map((o) => {
          const on = state.stops.includes(o.key);
          const value = counts.stops[o.key];
          return (
            <button
              key={o.key}
              type="button"
              className={`ffp-check${on ? ' is-on' : ''}`}
              aria-pressed={on}
              disabled={value.n === 0 && !on}
              onClick={() => {
                onPatch({ stops: toggle(state.stops, o.key) });
              }}
            >
              <span className="ffp-checkbox" aria-hidden="true">
                <CheckIcon size={11} aria-hidden />
              </span>
              <span className="ffp-check-name">{copy.stops[o.label]}</span>
              {value.from !== null && currency ? (
                <span className="ffp-cost">{fmtMoneyWhole({ amount: value.from, currency })}</span>
              ) : null}
            </button>
          );
        })}
      </fieldset>
    </PanelSection>
  );
}

/**
 * Companhias — o ÚNICO eixo sem contagem nem "a partir de", e é deliberado.
 *
 * Contar por companhia é uma pergunta por linha ao provider (a lista é do conjunto
 * da busca, não da página), então numa rota internacional o eixo custava uma
 * sonda por cia a cada corte. O que existia no lugar era pior que a ausência: um
 * teto de 12 apurava as primeiras e as demais herdavam o total da busca — sete
 * linhas anunciando o mesmo "155", que é a contagem do conjunto inteiro e não da
 * companhia, ao lado de vizinhas com o número certo.
 *
 * Sem número não há por que desabilitar linha nenhuma: uma opção inerte sem
 * contagem ao lado não se explica. Toda companhia da rota fica clicável, e um
 * corte que não sobra nada se desfaz no mesmo clique.
 *
 * O MENOR PREÇO da linha (mock 2107) sai da PÁGINA em mão (`airlineOptions`),
 * como o das paradas — companhia do conjunto sem oferta na página fica sem
 * preço, que é a ausência honesta (o placeholder do fio é o total da busca).
 */
function AirlineSection({
  offers,
  state,
  currency,
  onPatch,
  serverAirlines,
  copy,
}: {
  currency: string | undefined;
  offers: readonly FlightOfferSource[];
  /** Companhias do CONJUNTO da busca. Derivar da página faria a lista encolher a
   *  cada corte — marcar GOL apagaria LATAM e não haveria como desmarcar.
   *  `[]` degrada pro MESMO lugar que `undefined`: o fio nunca manda ausência de
   *  verdade (`ModalSessionFlightFacetsSchema.airlines` é `z.array(...)`, sem
   *  opcional), então uma busca de companhias que falhou no provider chega aqui
   *  como array VAZIO, não como `undefined` — um `??` sozinho aceitaria isso
   *  como resposta válida e a seção sumiria (`length <= 1` abaixo) mesmo com
   *  companhias de sobra nas ofertas em mão. */
  serverAirlines?: readonly { code: string; name: string }[];
} & SectionProps) {
  const pageAirlines = airlineOptions(offers);
  const fromByCode = new Map(pageAirlines.map((a) => [a.iata, a.from]));
  const airlines = serverAirlines?.length
    ? serverAirlines.map((a) => ({ iata: a.code, name: a.name }))
    : pageAirlines;
  // Paridade com o drawer atual: uma companhia só não é escolha.
  if (airlines.length <= 1) return null;
  return (
    <PanelSection label={copy.airline}>
      <fieldset className="ffp-checks" aria-label={copy.airline}>
        {airlines.map((a) => {
          const on = state.airlines.includes(a.iata);
          const from = fromByCode.get(a.iata);
          return (
            <button
              key={a.iata}
              type="button"
              className={`ffp-check${on ? ' is-on' : ''}`}
              aria-pressed={on}
              onClick={() => {
                onPatch({ airlines: toggle(state.airlines, a.iata) });
              }}
            >
              <span className="ffp-checkbox" aria-hidden="true">
                <CheckIcon size={11} aria-hidden />
              </span>
              <AirlineLogo iata={a.iata} name={a.name} size={18} />
              <span className="ffp-check-name">{a.name}</span>
              {from !== undefined && currency ? (
                <span className="ffp-cost">{fmtMoneyWhole({ amount: from, currency })}</span>
              ) : null}
            </button>
          );
        })}
      </fieldset>
    </PanelSection>
  );
}

function AirportSection({
  offers,
  state,
  counts,
  onPatch,
  copy,
  cityOf,
}: {
  offers: readonly FlightOfferSource[];
  /** A cidade de um IATA — o host passa o `cityByAirport` do domain. */
  cityOf: CityOfAirport;
  /** O eixo que desenha SÓ contagem — ver `FlightFilterOptionCounts` e `Cost`. */
  counts: FlightFilterOptionCounts;
} & SectionProps) {
  const groups = airportOptions(offers, cityOf);
  const total = groups.reduce((sum, g) => sum + g.airports.length, 0);
  // Rota A→B sem aeroporto alternativo: eixo sem escolha real é ruído.
  if (total <= 2) return null;
  return (
    <PanelSection label={copy.airports.label}>
      <fieldset className="ffp-checks" aria-label={copy.airports.label}>
        {groups.map((g) => (
          <div key={g.city} className="ffp-city">
            <span className="ffp-city-label">
              {g.city === 'other' ? copy.airports.otherCities : g.city}
            </span>
            {g.airports.map((iata) => {
              const on = state.airports.includes(iata);
              const value = counts.airports[iata] ?? { n: 0, from: null };
              return (
                <button
                  key={iata}
                  type="button"
                  className={`ffp-check${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  disabled={value.n === 0 && !on}
                  onClick={() => {
                    onPatch({ airports: toggle(state.airports, iata) });
                  }}
                >
                  <span className="ffp-checkbox" aria-hidden="true">
                    <CheckIcon size={11} aria-hidden />
                  </span>
                  <span className="ffp-check-name">{iata}</span>
                  <Cost value={value} />
                </button>
              );
            })}
          </div>
        ))}
      </fieldset>
    </PanelSection>
  );
}
